import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import MasterViewPage from "../common/MasterViewPage";
import StatusBadge from "../common/StatusBadge";
import { getDistrictById } from "../../services/districtService";
import {
  extractApiErrorMessage,
  formatMasterDate,
} from "../../utils/masterHelpers";
import "./Districts.css";

export default function DistrictView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", open: false, severity: "error" });

  const loadDistrict = useCallback(async () => {
    setLoading(true);

    try {
      if (!id) {
        setToast({
          message: "District ID is missing.",
          open: true,
          severity: "error",
        });
        setDistrict(null);
        return;
      }

      const selectedDistrict = await getDistrictById(id);

      if (!selectedDistrict) {
        setToast({
          message: "District not found.",
          open: true,
          severity: "error",
        });
      }

      setDistrict(selectedDistrict || null);
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(apiError, "Failed to load district details."),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDistrict();
  }, [loadDistrict]);

  const fields = district
    ? [
        { label: "Code", value: district.code || "NA" },
        { label: "Name", value: district.name || "NA" },
        { label: "State", value: district.state || "NA" },
        { label: "Status", value: <StatusBadge value={district.is_active} /> },
        { label: "Created At", value: formatMasterDate(district.created_at) },
      ]
    : [];

  return (
    <MasterShell pageClassName="districts-module">
      {loading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading district</h2>
          <p className="masters-state__text">
            Please wait while district details are being loaded.
          </p>
        </div>
      ) : district ? (
        <MasterViewPage
          description="District master details with the same styling language used across the current application."
          fields={fields}
          onBack={() => navigate("/districts")}
          title="View District"
        />
      ) : (
        <div className="masters-state">
          <h2 className="masters-state__title">District not found</h2>
          <p className="masters-state__text">
            The selected district could not be found in the current response.
          </p>
        </div>
      )}

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
